import { motion } from 'framer-motion';
import { Layers, Cpu, TrendingUp, ArrowRight } from 'lucide-react';

const steps = [
  {
    num: '01',
    icon: Layers,
    title: 'White-Labeled Human + AI Talent',
    subtitle: 'Output Layer',
    description:
      'Pre-vetted operators across design, content, SEO, engineering, and automation — deployed under your brand and integrated into your client workflows in 48 hours.',
    bullets: [
      'Designers, writers, SEO specialists, engineers, and AI operators',
      'Works inside your tools, stack, and processes',
      'White-labeled and client-ready from day one',
    ],
  },
  {
    num: '02',
    icon: Cpu,
    title: 'Your Execution Layer',
    subtitle: 'Talent Management System',
    description:
      'A dedicated Talent Manager runs all coordination, quality control, and delivery logistics behind the scenes — so your agency never manages talent directly.',
    bullets: [
      'Tracks hours, output, and delivery milestones',
      'Enforces quality standards across every engagement',
      'Handles payments, workflows, and operational coordination',
    ],
  },
  {
    num: '03',
    icon: TrendingUp,
    title: 'You Control Strategy. We Run Operations.',
    subtitle: 'Zero Management Overhead',
    description:
      'Focus entirely on client relationships and growth — HireX8 absorbs all the operational weight of scaling a delivery team.',
    bullets: [
      'No hiring, no recruiting, no HR overhead',
      'No talent management or performance reviews',
      'More capacity, higher margins, less internal friction',
    ],
  },
];

export const HowItWorksSection = () => (
  <section id="how-it-works" className="py-24 md:py-32 bg-apple-surface border-t border-border">
    <div className="section-container">
      {/* Header */}
      <motion.div
        className="mb-16"
        initial={{ opacity: 0, y: 20 }}
        whileInView={{ opacity: 1, y: 0 }}
        viewport={{ once: true }}
        transition={{ duration: 0.6 }}
      >
        <p className="section-label mb-5">How It Works</p>
        <h2 className="text-[42px] sm:text-[52px] font-extrabold tracking-tighter text-foreground leading-none mb-5">
          Infrastructure That Runs<br />Your Agency's Delivery.
        </h2>
        <p className="text-[17px] text-muted-foreground leading-[1.55] max-w-xl">
          Three layers. One system. You own the client — HireX8 runs the execution.
        </p>
      </motion.div>

      {/* Step list */}
      <div className="flex flex-col">
        {steps.map((step, i) => (
          <motion.div
            key={step.num}
            className="grid md:grid-cols-[80px_1fr_2fr] items-start gap-6 md:gap-10 py-10 border-t border-border"
            initial={{ opacity: 0, x: -20 }}
            whileInView={{ opacity: 1, x: 0 }}
            viewport={{ once: true }}
            transition={{ delay: i * 0.12, duration: 0.6 }}
          >
            {/* Step number */}
            <div className="flex items-center">
              <div className="w-10 h-10 rounded-full border-2 border-apple-blue flex items-center justify-center flex-shrink-0">
                <span className="text-[12px] font-bold text-apple-blue">{step.num}</span>
              </div>
            </div>

            {/* Title block */}
            <div>
              <step.icon size={22} className="text-apple-blue mb-3" />
              <h3 className="text-[20px] font-bold text-foreground tracking-tight leading-tight mb-1">
                {step.title}
              </h3>
              <p className="text-[13px] font-semibold uppercase tracking-[0.08em] text-muted-foreground">
                {step.subtitle}
              </p>
            </div>

            {/* Description + bullets */}
            <div className="md:pt-1">
              <p className="text-[16px] text-muted-foreground leading-[1.65] mb-4">
                {step.description}
              </p>
              <ul className="space-y-2">
                {step.bullets.map(b => (
                  <li key={b} className="flex items-start gap-2 text-[14px] text-muted-foreground">
                    <span className="w-1.5 h-1.5 rounded-full bg-apple-blue flex-shrink-0 mt-[7px]" />
                    {b}
                  </li>
                ))}
              </ul>
            </div>
          </motion.div>
        ))}
        <div className="swiss-divider" />
      </div>

      {/* CTA */}
      <motion.div
        className="mt-12"
        initial={{ opacity: 0, y: 16 }}
        whileInView={{ opacity: 1, y: 0 }}
        viewport={{ once: true }}
        transition={{ delay: 0.3, duration: 0.5 }}
      >
        <a href="https://vibe.hirex8.com/register?type=client" className="btn-primary inline-flex">
          Activate Your Execution Layer <ArrowRight size={16} className="ml-1" />
        </a>
      </motion.div>
    </div>
  </section>
);
